import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const reveal = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

const PILLARS = [
  { n: "01", t: "Strategy",
    items: ["Campaign Strategy", "Creative Strategy", "Audience Research", "Brand Positioning", "Program Development"],
    caseSlug: "ditch-the-screens", caseLabel: "Ditch the Screens · Scouts Canada" },
  { n: "02", t: "Story",
    items: ["Brand Films", "Campaign Films", "Photography", "Creative Direction", "Social Content", "Copywriting"],
    caseSlug: "chipmunks", caseLabel: "Chipmunks · Scouts Canada" },
  { n: "03", t: "Growth",
    items: ["Paid Social", "Lead Generation", "Membership Acquisition", "Volunteer Recruitment", "Fundraising Campaigns"],
    caseSlug: "scout-popcorn", caseLabel: "Scout Popcorn · Scouts Canada" },
  { n: "04", t: "Journey",
    items: ["Landing Pages", "Email Marketing", "CRM Journeys", "Lifecycle Marketing", "Lead Nurture", "Conversion Strategy", "Onboarding"],
    caseSlug: "scouts-volunteer-recruitment", caseLabel: "Volunteer Recruitment · Scouts Canada" },
];

const STUDIO_CAPABILITIES = [
  "Brand Films", "Campaign Films", "Automotive Storytelling", "Luxury Editorial Photography",
  "Editorial Magazine Publishing", "Campaign Strategy", "Fundraising Strategy",
  "Membership Acquisition", "Volunteer Recruitment", "Program Development",
  "Landing Pages", "Email + CRM", "Paid Social", "Lifecycle Marketing",
];

export default function Services() {
  return (
    <main data-testid="services-page" className="bg-black pt-32 pb-8">
      {/* HERO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-24">
        <motion.p {...reveal} className="overline mb-8">Good Bandit · Services</motion.p>
        <motion.h1 {...reveal} className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95]">
          Strategy.<br />
          Story.<br />
          Growth.<br />
          <span className="italic text-[var(--gb-gold)]">Experience.</span>
        </motion.h1>
        <motion.p {...reveal} className="text-white/65 leading-relaxed mt-10 max-w-2xl">
          A creative studio with serious marketing capability. We build campaigns,
          films, editorial and digital journeys that turn ideas into action.
        </motion.p>
      </section>

      {/* PILLARS */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 divide-y divide-white/10">
          {PILLARS.map((p) => (
            <motion.div {...reveal} key={p.n} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-16 md:py-24" data-testid={`services-pillar-${p.n}`}>
              <div className="md:col-span-3">
                <p className="kbd text-white/40">{p.n} /</p>
                <h2 className="font-display text-5xl md:text-6xl text-white leading-[0.95] mt-2">{p.t}.</h2>
              </div>
              <div className="md:col-span-5">
                <ul className="space-y-3 text-white/75 text-lg leading-relaxed">
                  {p.items.map((i) => <li key={i} className="flex gap-3"><span className="text-[var(--gb-gold)]">·</span> {i}</li>)}
                </ul>
              </div>
              <div className="md:col-span-4 md:border-l md:border-white/10 md:pl-8">
                <p className="overline mb-3">Selected work</p>
                <Link to={`/impact/${p.caseSlug}`} className="gb-link inline-flex items-center gap-2 text-white hover:text-[var(--gb-gold)] transition-colors">
                  {p.caseLabel} <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STUDIO CAPABILITIES */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <motion.div {...reveal} className="md:col-span-5">
            <p className="overline mb-4">Studio</p>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-[1]">
              Automotive storytelling meets <span className="italic text-[var(--gb-gold)]">growth.</span>
            </h3>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-6 md:col-start-7">
            <p className="text-white/65 leading-relaxed">
              Good Bandit brings brand film, luxury photography and editorial publishing
              into the same practice as campaign strategy, CRM and paid media. Every
              engagement is scoped from the story that needs to be told and the action
              it needs to move.
            </p>
            <div className="flex flex-wrap gap-2 mt-8">
              {STUDIO_CAPABILITIES.map((c) => (
                <span key={c} className="kbd border border-white/10 px-3 py-1.5 text-white/60">{c}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CROSS-LINK TO NOCTURNE */}
      <section className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-2 gap-10">
          <motion.div {...reveal}>
            <p className="kbd text-white/40 mb-3">Also from the studio</p>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-[1.05]">
              Some stories build desire.
              <br /><span className="italic text-[var(--gb-gold)]">Others build movements.</span>
            </h3>
          </motion.div>
          <motion.div {...reveal} className="md:justify-self-end md:text-right space-y-3">
            <Link to="/nocturne" className="gb-btn-outline inline-flex text-[10px]">Enter Nocturne <ArrowRight size={12} /></Link>
            <div><Link to="/impact" className="gb-btn-outline inline-flex text-[10px]">Explore Impact <ArrowRight size={12} /></Link></div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
